import {CustomFieldTO} from 'generated/JavaTO'
import _ from 'lodash'
import {CF} from 'module/CustomField/util/CustomField'
import {CFAttrKey} from 'module/CustomField/constant/CFAttrConstants'

interface CFDefCell {
	cfDef: CustomFieldTO
	rowPos: number
	colPos: number
	rowSpan: number
	colSpan: number
}

interface CFDefGroupedCell {
	cfDefCells: CFDefCell[]
	groupId?: number | null
	rowPos: number
	colPos: number
	rowSpan: number
	colSpan: number
}

function getHPosIndex(hpos: number, positions: (number | null)[]) {
	let ret = positions.indexOf(hpos)
	if (ret > -1) return ret
	for (let pos = 0; pos < positions.length; pos++) {
		const x = positions[pos]
		if (x && x > hpos) return pos
	}
	return positions.length
}

function sortCFDef(a: CustomFieldTO, b: CustomFieldTO) {
	return a.vpos - b.vpos || a.hpos - b.hpos
}

function findColSpan(hposByVpos: Record<number, (number | null)[]>) {
	return Object.values(hposByVpos).reduce((s, hposRest) => {
		const index = hposRest.indexOf(null)
		const span = hposRest.lastIndexOf(null) - index + 1
		if (span > s) return span
		else return s
	}, 0)
}

function findRowSpan(hposByVpos: Record<number, (number | null)[]>) {
	return Object.entries(hposByVpos).flatMap(([vpos, hposRest]) => {
		const index = hposRest.indexOf(null)
		if (index > -1) return [vpos]
		else return []
	}).length || 1
}

/**
 * pass only visible cfdefs. filter cfDef.visible before rendering
 */
export function getCFDefGrid(customFieldDefinitions: CustomFieldTO[], trimCellsLeft?: boolean) {
	const hposAll: number[] = []
	const vposAll: number[] = []
	const {single, byGroup} = customFieldDefinitions.reduce((s, cfDef) => {
		if (cfDef.cfGroupId != null) {
			if (!s.byGroup[cfDef.cfGroupId]) s.byGroup[cfDef.cfGroupId] = [cfDef]
			else s.byGroup[cfDef.cfGroupId].push(cfDef)
		} else {
			s.single.push(cfDef)
		}
		if (!hposAll.includes(cfDef.hpos)) hposAll.push(cfDef.hpos)
		if (!vposAll.includes(cfDef.vpos)) vposAll.push(cfDef.vpos)
		return s
	}, {
		single: [] as CustomFieldTO[],
		byGroup: {} as Record<number, CustomFieldTO[]>,
	})
	hposAll.sort((a, b) => a - b)
	vposAll.sort((a, b) => a - b)
	const groups = Object.values(byGroup)
	groups.forEach(g => g.sort(sortCFDef))
	const allCFDefs = [...single.map(cfDef => [cfDef]), ...groups]
	allCFDefs.sort((a, b) => sortCFDef(a[0], b[0]))
	const hposByVpos = vposAll.reduce((s, vpos) => {
		s[vpos] = [...hposAll]
		return s
	}, {} as Record<number, (number | null)[]>)
	// find colspans
	const groupedCFDefs = allCFDefs.map((cfDefs) => {
		const hposByVposSingle = vposAll.reduce((s, vpos) => {
			s[vpos] = [...hposAll]
			return s
		}, {} as Record<number, (number | null)[]>)
		const first = cfDefs[0]
		const groupId = first.cfGroupId
		const cellConfig = cfDefs.reduce((s, cfDef) => {
			// find first hpos and length of group
			const colSpan = cfDef.colSpan == null ? 1 : cfDef.colSpan
			if (cfDef.hpos < s.hpos) s.hpos = cfDef.hpos
			if (cfDef.vpos < s.vpos) s.vpos = cfDef.vpos
			const positions = hposByVpos[cfDef.vpos]
			const positionsSingleLine = hposByVposSingle[cfDef.vpos]
			const hposIndex = getHPosIndex(cfDef.hpos, positions)
			const hposIndexSingleLine = getHPosIndex(cfDef.hpos, positionsSingleLine)
			for (let c = 0; c < colSpan; c++) {
				if (positions[hposIndex + c] != null) {
					positions[hposIndex + c] = null
				} else {
					// found overlapping cells -> add position
					positions.splice(hposIndex + c, 0, null)
				}
				if (positionsSingleLine[hposIndexSingleLine + c] != null) {
					positionsSingleLine[hposIndexSingleLine + c] = null
				} else {
					// found overlapping cells -> add position
					positionsSingleLine.splice(hposIndexSingleLine + c, 0, null)
				}
			}
			return s
		}, {hpos: Infinity, vpos: Infinity})
		const colSpan = findColSpan(hposByVposSingle)
		const rowSpan = findRowSpan(hposByVposSingle)
		return {cfDefs, groupId, ...cellConfig, colSpan, rowSpan}
	})
	const maxColSpan = findColSpan(hposByVpos)

	// find pre-content spacer colspan
	let lastVPos: number | null = null
	let hposNextFreeIndex: number = 0
	let groupedCFDefCells: CFDefGroupedCell[] = groupedCFDefs.map((p, i) => {
		const {cfDefs, groupId, colSpan, rowSpan, hpos, vpos} = p

		// find pre-filled spacers for inner grid
		let inner = {
			lastVPos: null as null | number,
			hposNextFreeIndex: 0,
			hposAll: hposAll.slice(hposAll.indexOf(hpos)),
			vposAll: _.uniq(cfDefs.map(cfDef => cfDef.vpos)).sort((a, b) => a - b),
			maxColSpan: colSpan,
		}
		const cfDefCells = cfDefs.map((cfDef, ii) => {
			const {vpos, hpos} = cfDef
			const rowSpan = cfDef.rowSpan == null ? 1 : cfDef.rowSpan
			const colSpan = cfDef.colSpan == null ? 1 : cfDef.colSpan
			if (inner.lastVPos !== vpos) {
				inner.hposNextFreeIndex = 0
				inner.lastVPos = vpos
			}
			//either hpos or take next free hpos
			const hposIndex = Math.max(inner.hposAll.indexOf(hpos), inner.hposNextFreeIndex)
			inner.hposNextFreeIndex = hposIndex + colSpan
			return {cfDef, rowPos: inner.vposAll.indexOf(vpos) + 1, colPos: hposIndex + 1, rowSpan, colSpan}
		})

		if (lastVPos !== vpos) {
			hposNextFreeIndex = 0
			lastVPos = vpos
		}
		//either hpos or take next free hpos
		const hposIndex = Math.max(hposAll.indexOf(hpos), hposNextFreeIndex)
		hposNextFreeIndex = hposIndex + colSpan
		return {cfDefCells, groupId, rowPos: vposAll.indexOf(vpos) + 1, colPos: hposIndex + 1, rowSpan, colSpan}
	})
	if (trimCellsLeft) {
		let currentRow = NaN
		let currentCol = 1
		groupedCFDefCells = groupedCFDefCells.map(x => {
			if (x.rowPos !== currentRow) {
				currentRow = x.rowPos
				currentCol = 1
			}
			const ret = {...x, colPos: currentCol}
			currentCol += x.colSpan
			return ret
		})
	}
	return {
		maxColSpan,
		groupedCFDefCells,
	}
}